<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('morseCode.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('morseCode.title') }}</h1>
      <p>{{ $t('morseCode.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="mode-bar">
        <label class="mode-label">{{ $t('morseCode.modeLabel') }}</label>
        <div class="mode-tabs">
          <button
            :class="['mode-tab', { active: mode === 'basic' }]"
            @click="mode = 'basic'"
          >{{ $t('morseCode.modeBasic') }}</button>
          <button
            :class="['mode-tab', { active: mode === 'chinese' }]"
            @click="mode = 'chinese'"
          >{{ $t('morseCode.modeChinese') }}</button>
        </div>
        <span class="mode-hint">{{ mode === 'basic' ? $t('morseCode.modeBasicHint') : $t('morseCode.modeChineseHint') }}</span>
      </div>

      <div class="codec-layout">
        <div class="codec-panel">
          <label>{{ $t('morseCode.textLabel') }}</label>
          <textarea v-model="text" :placeholder="mode === 'basic' ? $t('morseCode.textPlaceholder') : $t('morseCode.textPlaceholderCn')" rows="8" spellcheck="false"></textarea>
        </div>

        <div class="codec-actions">
          <button class="btn btn-primary" @click="toMorse">{{ $t('morseCode.encode') }} →</button>
          <button class="btn btn-secondary" @click="fromMorse">← {{ $t('morseCode.decode') }}</button>
          <button class="btn btn-accent" @click="playMorse" :disabled="!morse || playing">{{ playing ? '🔊' : '▶️' }} {{ $t('morseCode.play') }}</button>
        </div>

        <div class="codec-panel">
          <label>{{ $t('morseCode.morseLabel') }}</label>
          <textarea v-model="morse" :placeholder="$t('morseCode.morsePlaceholder')" rows="8" spellcheck="false"></textarea>
        </div>
      </div>

      <div class="action-bar">
        <span class="status-msg" v-if="statusMsg">{{ statusMsg }}</span>
        <button class="btn btn-outline" @click="copyMorse">{{ $t('common.copy') }}</button>
        <button class="btn btn-outline" @click="clear">{{ $t('common.clear') }}</button>
      </div>

      <div class="morse-chart">
        <h3>{{ $t('morseCode.chartTitle') }}</h3>
        <div class="chart-grid">
          <div class="chart-item" v-for="(code, char) in morseMap" :key="char">
            <span class="chart-char">{{ char }}</span>
            <span class="chart-code">{{ code }}</span>
          </div>
        </div>
      </div>

      <div class="chinese-encoding-info" v-if="mode === 'chinese'">
        <h3>{{ $t('morseCode.cnEncodingTitle') }}</h3>
        <p>{{ $t('morseCode.cnEncodingDesc') }}</p>
        <div class="cn-example">
          <div class="cn-example-row" v-for="(item, i) in cnExamples" :key="i">
            <span class="cn-char">{{ item.char }}</span>
            <span class="cn-arrow">→</span>
            <span class="cn-unicode">U+{{ item.hex }}</span>
            <span class="cn-arrow">→</span>
            <span class="cn-morse">{{ item.morse }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('morseCode.whatIs') }}</h2>
      <p>{{ $t('morseCode.whatIsDesc') }}</p>
      <h3>{{ $t('morseCode.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('morseCode.useCases')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
const MORSE_MAP = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.', 'G': '--.', 'H': '....',
  'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..', 'M': '--', 'N': '-.', 'O': '---', 'P': '.--.',
  'Q': '--.-', 'R': '.-.', 'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
  'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---', '3': '...--', '4': '....-',
  '5': '.....', '6': '-....', '7': '--...', '8': '---..', '9': '----.', '.': '.-.-.-', ',': '--..--',
  '?': '..--..', "'": '.----.', '!': '-.-.--', '/': '-..-.', '(': '-.--.', ')': '-.--.-', '&': '.-...',
  ':': '---...', ';': '-.-.-.', '=': '-...-', '+': '.-.-.', '-': '-....-', '_': '..--.-', '"': '.-..-.',
  '$': '...-..-', '@': '.--.-.'
}
const REVERSE_MAP = {}
Object.keys(MORSE_MAP).forEach(k => { REVERSE_MAP[MORSE_MAP[k]] = k })

// Chinese punctuation to ASCII mapping
const CN_PUNCT_MAP = {
  '\uFF0C': ',', '\u3002': '.', '\uFF1F': '?', '\uFF01': '!', '\uFF1A': ':', '\uFF1B': ';',
  '\u201C': '"', '\u201D': '"', '\u2018': "'", '\u2019': "'", '\uFF08': '(', '\uFF09': ')',
  '\u3001': ',', '\u2026': '...', '\u2014': '-', '\uFF5E': '-'
}

function isCJK(c) {
  const code = c.codePointAt(0)
  return (code >= 0x4E00 && code <= 0x9FFF) ||   // CJK Unified Ideographs
         (code >= 0x3400 && code <= 0x4DBF) ||   // CJK Extension A
         (code >= 0x20000 && code <= 0x2A6DF) || // CJK Extension B
         (code >= 0x2A700 && code <= 0x2B73F) || // CJK Extension C
         (code >= 0xF900 && code <= 0xFAFF) ||   // CJK Compatibility Ideographs
         (code >= 0x3000 && code <= 0x303F) ||   // CJK Symbols and Punctuation
         (code >= 0xFF00 && code <= 0xFFEF)      // Fullwidth Forms
}

// Encode a single Unicode code point to Morse via hex digits
// Format: <hex1 hex2 hex3 hex4> wrapped in special markers
function unicodeCharToMorse(c) {
  const hex = c.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')
  const morseDigits = hex.split('').map(h => MORSE_MAP[h] || '').filter(Boolean).join(' ')
  return '[' + morseDigits + ']'
}

// Decode a [....] Morse block back to a Unicode character
function morseBlockToChar(block) {
  const inner = block.slice(1, -1).trim()
  const codes = inner.split(/\s+/)
  const hex = codes.map(c => REVERSE_MAP[c] || '?').join('')
  const codePoint = parseInt(hex, 16)
  if (isNaN(codePoint) || codePoint === 0) return '?'
  return String.fromCodePoint(codePoint)
}

export default {
  name: 'MorseCodeTool',
  data() {
    return { text: '', morse: '', statusMsg: '', playing: false, mode: 'basic' }
  },
  computed: {
    morseMap() { return MORSE_MAP },
    cnExamples() {
      const chars = ['中', '文', '你', '好']
      return chars.map(c => {
        const hex = c.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')
        const morseDigits = hex.split('').map(h => MORSE_MAP[h]).join(' ')
        return { char: c, hex, morse: '[' + morseDigits + ']' }
      })
    }
  },
  methods: {
    toMorse() {
      if (!this.text.trim()) return

      if (this.mode === 'basic') {
        // Basic mode: only ASCII letters, digits, punctuation
        const result = this.text.toUpperCase().split('').map(c => {
          if (c === ' ') return '/'
          return MORSE_MAP[c] || ''
        }).filter(Boolean).join(' ')
        this.morse = result
      } else {
        // Chinese mode: support CJK characters via Unicode encoding
        const parts = []
        for (const c of this.text) {
          if (c === ' ' || c === '\u3000') {
            // Space or fullwidth space
            parts.push('/')
          } else if (CN_PUNCT_MAP[c]) {
            // Chinese punctuation → map to ASCII equivalent
            const ascii = CN_PUNCT_MAP[c]
            for (const a of ascii) {
              if (MORSE_MAP[a.toUpperCase()]) {
                parts.push(MORSE_MAP[a.toUpperCase()])
              }
            }
          } else if (isCJK(c)) {
            // CJK character → encode as [hex morse]
            parts.push(unicodeCharToMorse(c))
          } else {
            // Regular ASCII
            const upper = c.toUpperCase()
            if (MORSE_MAP[upper]) {
              parts.push(MORSE_MAP[upper])
            }
          }
        }
        this.morse = parts.join(' ')
      }
      this.statusMsg = this.$t('morseCode.encodeSuccess')
    },
    fromMorse() {
      if (!this.morse.trim()) return

      if (this.mode === 'basic') {
        const words = this.morse.trim().split(/\s*\/\s*/)
        const result = words.map(word =>
          word.trim().split(/\s+/).map(code => REVERSE_MAP[code] || '?').join('')
        ).join(' ')
        this.text = result
      } else {
        // Chinese mode: parse [....] blocks and regular Morse
        const input = this.morse.trim()
        let result = ''
        let i = 0
        while (i < input.length) {
          if (input[i] === '[') {
            // Find the matching ]
            const end = input.indexOf(']', i)
            if (end === -1) {
              result += '?'
              i++
            } else {
              const block = input.substring(i, end + 1)
              result += morseBlockToChar(block)
              i = end + 1
              // Skip space after block
              if (i < input.length && input[i] === ' ') i++
            }
          } else if (input[i] === '/') {
            result += ' '
            i++
            // Skip surrounding spaces
            while (i < input.length && input[i] === ' ') i++
          } else if (input[i] === ' ') {
            i++
          } else {
            // Collect a Morse code unit (dots and dashes)
            let code = ''
            while (i < input.length && input[i] !== ' ' && input[i] !== '/' && input[i] !== '[') {
              code += input[i]
              i++
            }
            if (code) {
              result += REVERSE_MAP[code] || '?'
            }
            // Skip space
            if (i < input.length && input[i] === ' ') i++
          }
        }
        this.text = result
      }
      this.statusMsg = this.$t('morseCode.decodeSuccess')
    },
    async playMorse() {
      if (!this.morse || this.playing) return
      this.playing = true
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const dot = 0.08, dash = 0.24, gap = 0.08, charGap = 0.24, wordGap = 0.56
      let t = ctx.currentTime + 0.1

      for (const c of this.morse) {
        if (c === '.') {
          this.beep(ctx, t, dot); t += dot + gap
        } else if (c === '-') {
          this.beep(ctx, t, dash); t += dash + gap
        } else if (c === '/') {
          t += wordGap
        } else if (c === ' ') {
          t += charGap
        }
        // Skip [ ] markers during playback (they are structural)
      }
      setTimeout(() => { this.playing = false; ctx.close() }, (t - ctx.currentTime) * 1000 + 200)
    },
    beep(ctx, time, duration) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = 600
      gain.gain.value = 0.3
      osc.start(time)
      osc.stop(time + duration)
    },
    clear() { this.text = ''; this.morse = ''; this.statusMsg = '' },
    async copyMorse() {
      if (!this.morse) return
      try {
        await navigator.clipboard.writeText(this.morse)
        this.statusMsg = this.$t('common.copied')
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1000px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }
.codec-layout { display: flex; gap: 16px; align-items: stretch; }
.codec-panel { flex: 1; display: flex; flex-direction: column; }
.codec-panel label { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.codec-panel textarea { flex: 1; min-height: 180px; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 14px; resize: vertical; outline: none; box-sizing: border-box; }
.codec-panel textarea:focus { border-color: var(--accent-color); }
.codec-actions { display: flex; flex-direction: column; justify-content: center; gap: 10px; }
.btn { padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; white-space: nowrap; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-secondary:hover { border-color: var(--accent-color); }
.btn-accent { background: #10b981; color: white; }
.btn-accent:hover { opacity: 0.9; }
.btn-accent:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-outline { background: transparent; color: var(--text-primary); border: 1px solid var(--border-color); padding: 8px 16px; }
.btn-outline:hover { border-color: var(--accent-color); color: var(--accent-color); }
.action-bar { display: flex; align-items: center; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); flex: 1; }

.mode-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--border-color); flex-wrap: wrap; }
.mode-label { font-size: 14px; font-weight: 600; color: var(--text-primary); white-space: nowrap; }
.mode-tabs { display: flex; gap: 0; border: 1px solid var(--border-color); border-radius: 8px; overflow: hidden; }
.mode-tab { padding: 6px 16px; font-size: 13px; font-weight: 500; border: none; background: var(--bg-tertiary); color: var(--text-secondary); cursor: pointer; transition: all 0.2s; }
.mode-tab.active { background: var(--accent-color); color: white; }
.mode-tab:hover:not(.active) { background: var(--bg-secondary); }
.mode-hint { font-size: 12px; color: var(--text-tertiary); }

.morse-chart { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border-color); }
.morse-chart h3 { font-size: 16px; color: var(--text-primary); margin-bottom: 12px; }
.chart-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 8px; }
.chart-item { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: var(--bg-tertiary); border-radius: 6px; }
.chart-char { font-weight: 700; font-size: 14px; color: var(--accent-color); min-width: 16px; }
.chart-code { font-family: 'SF Mono', monospace; font-size: 13px; color: var(--text-secondary); }

.chinese-encoding-info { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border-color); }
.chinese-encoding-info h3 { font-size: 16px; color: var(--text-primary); margin-bottom: 8px; }
.chinese-encoding-info p { font-size: 13px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.cn-example { display: flex; flex-direction: column; gap: 8px; }
.cn-example-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--bg-tertiary); border-radius: 8px; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 13px; }
.cn-char { font-size: 18px; font-weight: 700; color: var(--accent-color); font-family: inherit; min-width: 24px; text-align: center; }
.cn-arrow { color: var(--text-tertiary); }
.cn-unicode { color: var(--text-secondary); min-width: 60px; }
.cn-morse { color: var(--text-primary); flex: 1; word-break: break-all; }

@media (max-width: 768px) {
  .codec-layout { flex-direction: column; }
  .codec-actions { flex-direction: row; justify-content: center; flex-wrap: wrap; }
  .mode-bar { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
